import createClient from '../client';
import cartDiscountCodesUpdateQuery from '../queries/cartDiscountCodesUpdateQuery';

interface ApplyDiscountsParams {
  token: string | undefined;
  discountCodes: string[];
}

interface ShopifyCart {
  token: string;
  items: Array<{
    id: number;
    quantity: number;
    discounts?: Array<{
      title: string;
    }>;
  }>;
  cart_level_discount_applications?: Array<{
    title: string;
  }>;
}

interface CartDetails {
  token: string | null;
  discountCodes: string[];
}

const getCartDetails = async (): Promise<CartDetails> => {
  try {
    const response = await fetch('/cart.json', {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    }
    const cart: ShopifyCart = await response.json();

    if (!cart.token.includes('?key=')) {
      console.error('Invalid cart token format: missing ?key=');
      return { token: null, discountCodes: [] };
    }

    // Get discount codes from cart items
    const itemDiscounts = cart.items
      .flatMap((item) => item.discounts || [])
      .map((discount) => discount.title);

    // Get cart level discount codes
    const cartLevelDiscounts = (
      cart.cart_level_discount_applications || []
    ).map((discount) => discount.title);

    // Combine and deduplicate discount codes
    const discountCodes = Array.from(
      new Set([...itemDiscounts, ...cartLevelDiscounts]),
    );

    return {
      token: cart.token,
      discountCodes,
    };
  } catch (error) {
    console.error(`Error fetching cart details: ${error}`);
    return { token: null, discountCodes: [] };
  }
};

const applyDiscounts = async ({
  token,
  discountCodes,
}: ApplyDiscountsParams) => {
  if (!token) {
    console.error('No token provided');
    return null;
  }
  const storefrontApiClient = createClient(token);

  if (!storefrontApiClient) {
    return null;
  }

  const { token: cartId, discountCodes: existingCodes } =
    await getCartDetails();
  if (!cartId) {
    console.error('Failed to get cart token');
    return null;
  }

  // Add the new discount code to existing ones
  const updatedDiscountCodes = Array.from(
    new Set([...discountCodes, ...existingCodes]),
  );

  try {
    // @ts-ignore - the types from the Shopify Storefront API client are not complete
    const response = await storefrontApiClient.request(
      cartDiscountCodesUpdateQuery(),
      {
        variables: {
          cartId: `gid://shopify/Cart/${cartId}`,
          discountCodes: updatedDiscountCodes,
        },
      },
    );

    if (response.errors) {
      console.error(
        `Failed to apply discounts: ${JSON.stringify(response.errors)}`,
      );
      return null;
    }

    return response.data?.cartDiscountCodesUpdate;
  } catch (error) {
    console.error(`Error applying discounts: ${error}`);
    return null;
  }
};

export default applyDiscounts;
